// ─────────────────────────────────────────────────────────────────────────────
// Crew — Typed browser API client (Agent A).
//
// One thin, fully-typed wrapper over the Worker's HTTP API. Every method maps to
// exactly one route in workers/api/index.ts and returns the SAME shape the server
// sends. Types come exclusively from the spine (./types, ./analytics, ./consent) —
// this module forks NOTHING.
//
// AUTH: the magic-link flow yields a bearer session token (POST /api/auth/verify
// → { token }). We hold it in memory and persist it to localStorage so a reload
// stays signed in; it is attached as `Authorization: Bearer <token>` on every
// authed request. Call setToken()/clearToken() to manage it explicitly.
//
// OPTIMISTIC CONCURRENCY: mutating versioned rows (goals, milestones, polls,
// intents) accept an optional `version`. Pass the version you last read; on a
// stale write the server responds 409 and this client throws ApiError(409) so the
// UI can reload + retry.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  Activity, BookingIntent, CardLink, Commitment, Consent, Entitlement,
  Goal, Group, Member, Milestone, PotEntry, TimePoll,
} from './types';
import type { FunnelEvent, FunnelEventProps } from './analytics';
import type { ConsentScope } from './types';

// ── Error surfaced to callers ────────────────────────────────────────────────
// Carries the HTTP status so the UI can branch (401 → re-auth, 402 → upsell,
// 403 → consent needed, 409 → reload + retry).
export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Token storage ────────────────────────────────────────────────────────────
const TOKEN_KEY = 'crew.session';

function readStoredToken(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  } catch {
    return null;
  }
}

let memToken: string | null = readStoredToken();

export function setToken(token: string | null): void {
  memToken = token;
  try {
    if (typeof localStorage === 'undefined') return;
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable (private mode / SSR) — in-memory token still works */
  }
}

export function clearToken(): void {
  setToken(null);
}

export function getToken(): string | null {
  return memToken;
}

export function isAuthed(): boolean {
  return memToken != null;
}

// ── Base URL ─────────────────────────────────────────────────────────────────
// Same-origin by default (the PWA and Worker share an origin in production). Set
// VITE_API_BASE for split-origin local dev (e.g. wrangler on :8787).
const API_BASE: string =
  (import.meta.env?.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

// ── Core fetch ───────────────────────────────────────────────────────────────
interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  // Extra headers (e.g. x-metrics-token for the metrics dashboard).
  headers?: Record<string, string>;
  // Skip attaching the bearer token (public routes).
  auth?: boolean;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, auth = true } = opts;
  const h: Record<string, string> = { ...headers };
  if (body !== undefined) h['content-type'] = 'application/json';
  if (auth && memToken) h['authorization'] = `Bearer ${memToken}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: h,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Response shapes — mirror the router's json(...) payloads exactly.
// ─────────────────────────────────────────────────────────────────────────────

export interface HealthResponse { ok: true; ts: string }
export interface MagicLinkResponse { ok: true; devToken: string }
export interface VerifyResponse { token: string; email: string }
export interface MeResponse { email: string; member: Member | null; group: Group | null }
export interface CreateGroupResponse { group: Group; member: Member; inviteCode: string }
export interface JoinGroupResponse { group: Group; member: Member }
export interface GetGroupResponse { group: Group; members: Member[]; entitlement: Entitlement }
export interface ConsentResponse { consents: Consent[] }
export interface SetConsentResponse { ok: true; consents: Consent[] }
export interface CardLinkResponse { cardLink: CardLink | null }
export interface PotResponse { entries: PotEntry[]; totalPence: number; note: string }
export interface ClickResponse { redirect: string; source: string }
export interface UnlockSpec {
  groupId: string; amountPence: number; currency: 'gbp'; description: string;
  successUrl: string; cancelUrl: string; configured: boolean;
}
export interface IngestEventResponse { persisted: boolean }
// One derived metric cell. `value: null` means the input feed does not exist yet
// (e.g. CAC has no ad-spend source) — the dashboard renders the `note` honestly
// instead of fabricating a number.
export interface DerivedMetricValue { value: number | null; unit: string; note?: string }
export interface MetricsResponse {
  funnel: Record<string, number>;
  conversions: Record<string, { count: number; valuePence: number; commissionPence: number }>;
  totals: { groups: number; unlockedGroups: number };
  derived: Record<string, DerivedMetricValue>;
}
export interface EraseResponse { erased: boolean }

// ─────────────────────────────────────────────────────────────────────────────
// The client.
// ─────────────────────────────────────────────────────────────────────────────
export const api = {
  // ── Health ─────────────────────────────────────────────────────────────────
  health(): Promise<HealthResponse> {
    return request('/api/health', { auth: false });
  },

  // ── Auth (magic link) ────────────────────────────────────────────────────────
  // Request a magic link. In dev the raw token is returned (devToken) for testing;
  // production delivery (Agent D) stops returning it.
  requestMagicLink(email: string): Promise<MagicLinkResponse> {
    return request('/api/auth/magic-link', { method: 'POST', body: { email }, auth: false });
  },

  // Exchange a magic-link token for a session bearer token. Stores it on success.
  async verify(token: string): Promise<VerifyResponse> {
    const res = await request<VerifyResponse>('/api/auth/verify', {
      method: 'POST', body: { token }, auth: false,
    });
    setToken(res.token);
    return res;
  },

  signOut(): void {
    clearToken();
  },

  // ── Session / self ───────────────────────────────────────────────────────────
  me(): Promise<MeResponse> {
    return request('/api/me');
  },

  // ── Right to erasure (UK-GDPR Art. 17) ───────────────────────────────────────
  // Wipes the caller's personal data server-side and invalidates the session, so
  // we also drop the local token (the user is signed out by the erasure itself).
  async eraseMe(): Promise<EraseResponse> {
    const res = await request<EraseResponse>('/api/me', { method: 'DELETE' });
    clearToken();
    return res;
  },

  // ── Groups (formation NEVER gated) ───────────────────────────────────────────
  createGroup(input: { name: string; displayName: string; goalSeeded?: boolean }): Promise<CreateGroupResponse> {
    return request('/api/groups', { method: 'POST', body: input });
  },

  // 402 (ApiError.status) if the group is at its seat cap.
  joinGroup(input: { inviteCode: string; displayName: string }): Promise<JoinGroupResponse> {
    return request('/api/groups/join', { method: 'POST', body: input });
  },

  getGroup(groupId: string): Promise<GetGroupResponse> {
    return request(`/api/groups/${encodeURIComponent(groupId)}`);
  },

  // ── Goals ────────────────────────────────────────────────────────────────────
  // 402 if the active-goal cap is reached.
  createGoal(groupId: string, input: {
    title: string; type?: Goal['type']; description?: string; targetDate?: string; seeded?: boolean;
  }): Promise<{ goal: Goal }> {
    return request(`/api/groups/${encodeURIComponent(groupId)}/goals`, { method: 'POST', body: input });
  },

  // Optimistic concurrency: pass `version` from the goal you last read. 409 on stale.
  patchGoal(goalId: string, input: {
    version?: number; title?: string; description?: string; targetDate?: string;
    status?: Goal['status']; participantIds?: string[];
  }): Promise<{ goal: Goal }> {
    return request(`/api/goals/${encodeURIComponent(goalId)}`, { method: 'PATCH', body: input });
  },

  commitGoal(goalId: string): Promise<{ ok: true }> {
    return request(`/api/goals/${encodeURIComponent(goalId)}/commit`, { method: 'POST', body: {} });
  },

  // ── Milestones ───────────────────────────────────────────────────────────────
  createMilestone(goalId: string, input: {
    title: string; dueDate?: string; ownerId?: string;
  }): Promise<{ milestone: Milestone }> {
    return request(`/api/goals/${encodeURIComponent(goalId)}/milestones`, { method: 'POST', body: input });
  },

  patchMilestone(milestoneId: string, input: {
    version?: number; title?: string; dueDate?: string; ownerId?: string;
    status?: Milestone['status']; completedBy?: string;
  }): Promise<{ milestone: Milestone }> {
    return request(`/api/milestones/${encodeURIComponent(milestoneId)}`, { method: 'PATCH', body: input });
  },

  // ── Time polls ───────────────────────────────────────────────────────────────
  createPoll(groupId: string, input: {
    title: string; goalId?: string; proposedSlots: TimePoll['proposedSlots'];
  }): Promise<{ poll: TimePoll }> {
    return request(`/api/groups/${encodeURIComponent(groupId)}/polls`, { method: 'POST', body: input });
  },

  // Upserts the caller's vote for one slot. 409 on stale `version`.
  votePoll(pollId: string, input: {
    slotId: string; vote?: 'yes' | 'maybe' | 'no'; memberId?: string; version?: number;
  }): Promise<{ poll: TimePoll }> {
    return request(`/api/polls/${encodeURIComponent(pollId)}/vote`, { method: 'POST', body: input });
  },

  // Locks the chosen slot (fires the time_locked funnel event server-side).
  lockPoll(pollId: string, input: { slotId: string; version?: number }): Promise<{ poll: TimePoll }> {
    return request(`/api/polls/${encodeURIComponent(pollId)}/lock`, { method: 'POST', body: input });
  },

  // ── Commitments (protected recurring time) ───────────────────────────────────
  // 402 if the commitment cap is reached.
  createCommitment(groupId: string, input: {
    title: string; cadence?: Commitment['cadence']; anchorISO?: string;
    goalId?: string; protected?: boolean;
  }): Promise<{ commitment: Commitment }> {
    return request(`/api/groups/${encodeURIComponent(groupId)}/commitments`, { method: 'POST', body: input });
  },

  // ── Booking intents (the proprietary high-intent signal) ─────────────────────
  createIntent(groupId: string, input: {
    activityId: string; goalId?: string; status?: BookingIntent['status'];
    estimatedHeads?: number; participantIds?: string[];
  }): Promise<{ intent: BookingIntent }> {
    return request(`/api/groups/${encodeURIComponent(groupId)}/intents`, { method: 'POST', body: input });
  },

  patchIntent(intentId: string, input: {
    version?: number; status?: BookingIntent['status'];
    estimatedHeads?: number; participantIds?: string[];
  }): Promise<{ intent: BookingIntent }> {
    return request(`/api/intents/${encodeURIComponent(intentId)}`, { method: 'PATCH', body: input });
  },

  // ── Activities (public suggestion feed; TINY seed — Agent C replaces) ────────
  suggestActivities(opts: { category?: Activity['category']; limit?: number } = {}): Promise<{ activities: Activity[] }> {
    const q = new URLSearchParams();
    if (opts.category) q.set('category', opts.category);
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return request(`/api/activities/suggest${qs ? `?${qs}` : ''}`, { auth: false });
  },

  // ── Attribution: click-out (records click_out + returns redirect URL) ────────
  attributionClick(input: {
    activityId: string; groupId: string; intentId?: string; url?: string;
  }): Promise<ClickResponse> {
    return request('/api/attribution/click', { method: 'POST', body: input });
  },

  // ── Consent (UK-GDPR: granular + revocable; server is source of truth) ───────
  getConsent(): Promise<ConsentResponse> {
    return request('/api/consent');
  },

  // Only analytics | card_link | marketing are settable (essential is always-on).
  setConsent(scope: Exclude<ConsentScope, 'essential'>, granted: boolean): Promise<SetConsentResponse> {
    return request('/api/consent', { method: 'POST', body: { scope, granted } });
  },

  // ── Card linking (consent-gated CLO readiness — NEVER issuing a card) ────────
  getCardLink(): Promise<CardLinkResponse> {
    return request('/api/cardlink');
  },

  // 403 if card_link consent has not been granted.
  linkCard(): Promise<{ cardLink: CardLink }> {
    return request('/api/cardlink', { method: 'POST', body: {} });
  },

  unlinkCard(): Promise<{ ok: true }> {
    return request('/api/cardlink', { method: 'DELETE' });
  },

  // ── Pot (TRACKING ONLY — never holds or moves money; entitlement-gated) ──────
  getPot(groupId: string): Promise<PotResponse> {
    return request(`/api/groups/${encodeURIComponent(groupId)}/pot`);
  },

  // 402 if pot tracking is not in the group's tier.
  addPotEntry(groupId: string, input: {
    amountPence: number; note?: string; goalId?: string; memberId?: string;
  }): Promise<{ entry: PotEntry }> {
    return request(`/api/groups/${encodeURIComponent(groupId)}/pot`, { method: 'POST', body: input });
  },

  // ── Web Push ─────────────────────────────────────────────────────────────────
  pushSubscribe(input: {
    groupId: string;
    subscription: { endpoint: string; keys: { p256dh: string; auth: string } };
  }): Promise<{ ok: true }> {
    return request('/api/push/subscribe', { method: 'POST', body: input });
  },

  // ── Billing: start the one-time per-group unlock (returns checkout spec) ──────
  startUnlock(groupId: string): Promise<UnlockSpec> {
    return request(`/api/groups/${encodeURIComponent(groupId)}/unlock`, { method: 'POST', body: {} });
  },

  // ── Funnel events (client SDK → server consent gate) ─────────────────────────
  // `persisted` is false if consent was denied or the event is unknown — the
  // server is the consent authority; the client never persists locally.
  ingestEvent<E extends FunnelEvent>(event: E, props: FunnelEventProps[E]): Promise<IngestEventResponse> {
    const { groupId, occurredAt, ...rest } = props;
    return request('/api/events', {
      method: 'POST',
      body: { event, groupId, occurredAt, props: rest },
    });
  },

  // ── Metrics (internal dashboard: session + METRICS_TOKEN) ────────────────────
  metrics(metricsToken?: string): Promise<MetricsResponse> {
    return request('/api/metrics', {
      headers: metricsToken ? { 'x-metrics-token': metricsToken } : {},
    });
  },
};

export type CrewApi = typeof api;
